import React, { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Sparkles, MessageSquare } from 'lucide-react';
import axios from 'axios';
import API_BASE from '../api';

const BRAND = '#c1009d';

const suggestions = [
  { icon: '🎓', text: 'Who can teach Writing Competition Workshop?' },
  { icon: '📅', text: 'Which coaches have availability this week?' },
  { icon: '🔍', text: "Find a coach for Author's Workshop in Pacific Time" },
  { icon: '⚡', text: 'Which coach has the fewest students right now?' },
];

function Chat({ activeSessionId, setActiveSessionId, refreshSessions }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const loadedRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => { inputRef.current?.focus(); }, [activeSessionId]);

  useEffect(() => {
    if (!activeSessionId) {
      setMessages([]);
      loadedRef.current = null;
      return;
    }
    if (loadedRef.current === activeSessionId) return;
    loadedRef.current = activeSessionId;

    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/chat/sessions/${activeSessionId}`);
        setMessages(res.data.session?.messages || []);
      } catch {
        setMessages([]);
      }
    })();
  }, [activeSessionId]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;
    const userMessage = text.trim();
    setInput('');

    const updatedMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(updatedMessages);
    setLoading(true);

    try {
      const conversationHistory = messages.map(m => ({ role: m.role, content: m.content }));
      const res = await axios.post(`${API_BASE}/api/chat`, {
        message: userMessage,
        conversation_history: conversationHistory,
        session_id: activeSessionId || null
      });

      const newSessionId = res.data.session_id;
      if (!activeSessionId && newSessionId) {
        loadedRef.current = newSessionId;
        setActiveSessionId(newSessionId);
      }

      setMessages([...updatedMessages, { role: 'assistant', content: res.data.response }]);
      refreshSessions();
    } catch (err) {
      setMessages([
        ...updatedMessages,
        { role: 'assistant', content: `Sorry, something went wrong: ${err.response?.data?.error || err.message}` }
      ]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleSubmit = (e) => { e.preventDefault(); sendMessage(input); };
  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(input); } };

  const isEmptyState = messages.length === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 60px)', margin: '-2rem', overflow: 'hidden' }}>
      <div style={{ flex: 1, overflowY: 'auto', padding: '1.5rem 0', display: 'flex', flexDirection: 'column' }}>
        {isEmptyState ? (
          <EmptyState onSuggestion={sendMessage} />
        ) : (
          <div style={{ maxWidth: 760, width: '100%', margin: '0 auto', padding: '0 1.5rem' }}>
            {messages.map((msg, i) => (
              <MessageBubble key={i} message={msg} />
            ))}
            {loading && <TypingIndicator />}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <div style={{ borderTop: '1px solid #e5e7eb', background: '#fff', padding: '1rem 1.5rem' }}>
        <form onSubmit={handleSubmit} style={{ maxWidth: 760, margin: '0 auto', display: 'flex', gap: '0.6rem', alignItems: 'flex-end' }}>
          <div style={inputWrapStyle}
            onFocus={e => { e.currentTarget.style.borderColor = BRAND; e.currentTarget.style.boxShadow = `0 0 0 3px ${BRAND}18`; }}
            onBlur={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.boxShadow = 'none'; }}
          >
            <MessageSquare size={18} color="#9ca3af" style={{ marginLeft: 12, flexShrink: 0 }} />
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about coaches, availability, or matching..."
              rows={1}
              disabled={loading}
              style={textareaStyle}
            />
          </div>
          <button type="submit" disabled={!input.trim() || loading} style={{
            width: 44, height: 44, borderRadius: 12, border: 'none',
            background: (!input.trim() || loading) ? '#e5e7eb' : BRAND,
            color: '#fff', cursor: (!input.trim() || loading) ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s', flexShrink: 0,
          }}>
            <Send size={18} />
          </button>
        </form>
        <p style={{ textAlign: 'center', fontSize: '0.7rem', color: '#b0b0b0', margin: '0.5rem auto 0', maxWidth: 760 }}>
          Powered by Claude AI · Real-time Calendly + Airtable data
        </p>
      </div>
    </div>
  );
}

// ── Empty State ──────────────────────────────────────────────────

function EmptyState({ onSuggestion }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div style={{ width: 64, height: 64, borderRadius: 20, background: `${BRAND}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem' }}>
        <Sparkles size={32} color={BRAND} />
      </div>
      <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937', marginBottom: '0.4rem' }}>Letterly AI Assistant</h2>
      <p style={{ color: '#6b7280', fontSize: '0.92rem', maxWidth: 420, textAlign: 'center', lineHeight: 1.6, marginBottom: '2rem' }}>
        Ask me about coaches, availability, student matching, or anything about the Letterly system.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', maxWidth: 520, width: '100%' }}>
        {suggestions.map((s, i) => (
          <button key={i} onClick={() => onSuggestion(s.text)} style={suggestionBtnStyle}
            onMouseEnter={e => { e.currentTarget.style.borderColor = BRAND; e.currentTarget.style.background = '#fdf2fb'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.background = '#fff'; }}
          >
            <span style={{ fontSize: '1.15rem', flexShrink: 0 }}>{s.icon}</span>
            <span>{s.text}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Message Components ───────────────────────────────────────────

function TypingIndicator() {
  return (
    <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem' }}>
      <Avatar role="assistant" />
      <div style={{ background: '#f3f4f6', borderRadius: '18px 18px 18px 4px', padding: '0.85rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <style>{`@keyframes blink { 0%,80%,100% { opacity:0.3 } 40% { opacity:1 } }`}</style>
        <span style={{ ...dotStyle, animationDelay: '0ms' }}>●</span>
        <span style={{ ...dotStyle, animationDelay: '200ms' }}>●</span>
        <span style={{ ...dotStyle, animationDelay: '400ms' }}>●</span>
      </div>
    </div>
  );
}

function MessageBubble({ message }) {
  const isUser = message.role === 'user';
  return (
    <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexDirection: isUser ? 'row-reverse' : 'row' }}>
      <Avatar role={message.role} />
      <div style={{
        maxWidth: '75%', padding: '0.85rem 1.15rem',
        borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
        background: isUser ? BRAND : '#f8f9fa', color: isUser ? '#fff' : '#1f2937',
        fontSize: '0.9rem', lineHeight: 1.7, wordBreak: 'break-word',
        boxShadow: isUser ? `0 2px 8px ${BRAND}30` : '0 1px 3px rgba(0,0,0,0.06)',
      }}>
        {isUser ? <span style={{ whiteSpace: 'pre-wrap' }}>{message.content}</span> : <FormattedReply content={message.content} />}
      </div>
    </div>
  );
}

function FormattedReply({ content }) {
  if (!content || typeof content !== 'string') return null;
  const lines = content.split('\n');
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();
    if (!trimmed) { i++; continue; }
    const bulletMatch = trimmed.match(/^([•\-*]|\d+\.)\s+(.+)/);
    if (bulletMatch) {
      const listItems = [];
      while (i < lines.length) {
        const m = lines[i].trim().match(/^([•\-*]|\d+\.)\s+(.+)/);
        if (!m) break;
        listItems.push(renderInlineFormat(m[2]));
        i++;
      }
      blocks.push(<ul key={blocks.length} style={{ margin: '0.25rem 0 0', paddingLeft: '1.25rem', listStyleType: 'disc' }}>{listItems.map((item, idx) => <li key={idx} style={{ marginBottom: '0.25rem' }}>{item}</li>)}</ul>);
      continue;
    }
    const paraLines = [];
    while (i < lines.length) {
      if (lines[i].trim().match(/^([•\-*]|\d+\.)\s+/)) break;
      if (lines[i].trim() === '' && paraLines.length > 0) { i++; break; }
      paraLines.push(lines[i]);
      i++;
    }
    const paraText = paraLines.join('\n').trim();
    if (paraText) blocks.push(<p key={blocks.length} style={{ margin: 0, padding: 0 }}>{renderInlineFormat(paraText)}</p>);
  }
  if (blocks.length === 0) return <span style={{ whiteSpace: 'pre-wrap' }}>{content}</span>;
  return <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>{blocks}</div>;
}

function renderInlineFormat(text) {
  const parts = [];
  const boldRegex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match;
  while ((match = boldRegex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(<strong key={match.index} style={{ fontWeight: 700 }}>{match[1]}</strong>);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length > 0 ? <>{parts}</> : text;
}

function Avatar({ role }) {
  const isUser = role === 'user';
  return (
    <div style={{
      width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: isUser ? '#1f2937' : BRAND, boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
    }}>
      {isUser ? <User size={16} color="#fff" /> : <Bot size={16} color="#fff" />}
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────

const inputWrapStyle = {
  flex: 1, display: 'flex', alignItems: 'center',
  border: '1.5px solid #e5e7eb', borderRadius: 14,
  padding: '0.1rem 0.25rem', background: '#fafafa',
  transition: 'border-color 0.2s, box-shadow 0.2s',
};

const textareaStyle = {
  flex: 1, border: 'none', outline: 'none', resize: 'none',
  padding: '0.7rem 0.6rem', fontSize: '0.9rem', fontFamily: 'inherit',
  background: 'transparent', lineHeight: 1.5,
};

const suggestionBtnStyle = {
  display: 'flex', alignItems: 'flex-start', gap: '0.6rem',
  padding: '0.85rem 1rem', borderRadius: 12,
  border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer',
  textAlign: 'left', transition: 'all 0.15s',
  fontSize: '0.82rem', color: '#374151', lineHeight: 1.45,
};

const dotStyle = { fontSize: '0.9rem', color: '#9ca3af', animation: 'blink 1.4s infinite' };

export default Chat;
